import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarApi, CalendarOptions, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';

import { SessionViewService } from '../../../core/services/academic-appointments/session-view.service';
import { SubjectChoiceService } from '../../../core/services/utils/subject-choice.service';
import {
  SchedulingSubject,
  displayTime,
  sessionEnd,
  sessionStart,
} from '../../../core/interfaces/academic-appointment';
import { ErrorStateComponent } from '../error-state/error-state.component';

/**
 * Ce que la grille sait montrer d'une séance. Les séances de l'administration
 * (`AcademicAppointment`) et celles de l'inspection (`InspectableSession`)
 * le satisfont telles quelles : c'est le dénominateur commun, pas un
 * troisième modèle.
 */
export interface CalendarSession {
  id: number;
  title: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  durationMinutes?: number | null;
  consultantName?: string | null;
  groupName?: string | null;
  attendeeCount?: number | null;
  subjectId?: number | null;
  /** Repli quand la liste des matières n'est pas rendue. */
  subjectName?: string | null;
  /** Matières de la séance, chacune avec son parcours — d'où le niveau. */
  subjects?: SchedulingSubject[] | null;
  status?: string;
  /** Inspectée ou non — montré quand l'hôte le demande (`inspection`). */
  isInspected?: boolean;
  /** Se tient en ce moment. */
  isOngoing?: boolean;
}

/** Période affichée, en dates ISO (`yyyy-MM-dd`), borne de fin exclue. */
export interface CalendarRange {
  from: string;
  to: string;
}

export type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

/**
 * Grille de calendrier des séances — mois, semaine, jour.
 *
 * <p>Elle ne charge rien : l'hôte reçoit la période affichée (`rangeChange`)
 * et lui rend les séances (`sessions`). La grille n'a donc à connaître ni le
 * service ni le droit qui ouvre les données — l'administration et
 * l'inspection partagent la même, et seul l'hôte diffère.</p>
 *
 * <p>Les pastilles portent l'heure, le titre, et — selon la durée, donc la
 * place — le professeur et le public ; en mode inspection, l'état inspectée
 * ou non, et un liseré quand la séance se tient en ce moment.</p>
 */
@Component({
  selector: 'app-sessions-calendar',
  standalone: true,
  imports: [CommonModule, TranslateModule, FullCalendarModule, ErrorStateComponent],
  templateUrl: './sessions-calendar.component.html',
  styleUrl: './sessions-calendar.component.css',
})
export class SessionsCalendarComponent {
  readonly view = inject(SessionViewService);
  private readonly subjectChoice = inject(SubjectChoiceService);

  @Input() set sessions(list: CalendarSession[] | null | undefined) {
    this.current = list ?? [];
    // Réassigner les options : le composant FullCalendar les compare et ne
    // redessine que ce qui change — ici les événements.
    this.calendarOptions = { ...this.calendarOptions, events: this.toEvents(this.current) };
  }

  @Input() loading = false;
  @Input() errored = false;
  /** Montre sur chaque pastille l'état inspectée / à inspecter. */
  @Input() inspection = false;

  @Output() readonly rangeChange = new EventEmitter<CalendarRange>();
  @Output() readonly select = new EventEmitter<CalendarSession>();
  @Output() readonly retry = new EventEmitter<void>();

  readonly periodLabel = signal('');
  readonly activeView = signal<CalendarView>('dayGridMonth');

  private current: CalendarSession[] = [];
  private api: CalendarApi | null = null;
  private lastRange: CalendarRange | null = null;

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin],
    initialView: 'dayGridMonth',
    locale: this.view.calendarLocale(),
    direction: this.view.isRtl() ? 'rtl' : 'ltr',
    headerToolbar: false,
    height: 'auto',
    firstDay: 1,
    dayMaxEvents: 3,
    nowIndicator: true,
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00',
    expandRows: true,
    // Des colonnes disjointes, pas des pastilles superposées : avec trois
    // séances à la même heure, le style « Google Agenda » rendait les titres
    // illisibles les uns par-dessus les autres.
    slotEventOverlap: false,
    eventDisplay: 'block',
    displayEventTime: false,
    events: [],
    eventClassNames: (arg) => {
      const s = arg.event.extendedProps as CalendarSession;
      return [this.view.colorClass(s), s.isOngoing ? 'is-ongoing' : ''].filter(Boolean);
    },
    eventContent: (arg) => this.renderEvent(
      arg.event.extendedProps as CalendarSession,
      arg.view.type.startsWith('dayGrid'),
    ),
    datesSet: (arg: DatesSetArg) => this.onDatesSet(arg),
    eventClick: (arg: EventClickArg) => this.select.emit(arg.event.extendedProps as CalendarSession),
  };

  // ─── Navigation ────────────────────────────────────────────────────

  prev(): void { this.api?.prev(); }
  next(): void { this.api?.next(); }
  today(): void { this.api?.today(); }

  switchView(view: CalendarView): void {
    this.activeView.set(view);
    this.api?.changeView(view);
  }

  /** La période affichée a changé : l'hôte recharge. Une fois par période, pas par rendu. */
  private onDatesSet(arg: DatesSetArg): void {
    this.api = arg.view.calendar;
    this.periodLabel.set(arg.view.title);
    const range: CalendarRange = { from: arg.startStr.slice(0, 10), to: arg.endStr.slice(0, 10) };
    if (this.lastRange?.from === range.from && this.lastRange?.to === range.to) return;
    this.lastRange = range;
    this.rangeChange.emit(range);
  }

  private toEvents(list: CalendarSession[]): EventInput[] {
    return list.map((s) => ({
      id: String(s.id),
      title: s.title,
      start: sessionStart(s),
      end: sessionEnd(s),
      extendedProps: s,
    }));
  }

  /**
   * Pastille d'une séance dans la grille.
   *
   * <p>Le nombre de lignes est décidé ici, d'après la <b>durée</b>, et non par
   * une règle de style d'après la hauteur rendue : en vue horaire la hauteur du
   * créneau est proportionnelle à la durée, et une pastille de quatre lignes
   * dans trente minutes déborde quoi qu'on lui dise ensuite. En vue mois la
   * hauteur ne dépend pas de la durée : tout y figure.</p>
   *
   * <p>La matière et son niveau passent en premier : c'est ce qui dit quelle
   * classe la séance concerne, et le nom du groupe manque à une séance
   * individuelle. Ce que la pastille tait figure dans son infobulle, et au
   * panneau de détail de l'hôte.</p>
   */
  private renderEvent(s: CalendarSession, month: boolean) {
    const time = displayTime(s.startTime);
    const who = s.consultantName ?? '';
    const target = s.groupName ?? (s.attendeeCount ? `${s.attendeeCount}` : '');
    const course = this.courseOf(s);

    const minutes = s.durationMinutes ?? 0;
    const roomy = month || minutes >= 45;
    const spacious = month || minutes >= 90;
    const ample = month || minutes >= 120;

    // Vu, ou à voir — une séance annulée n'est ni l'un ni l'autre.
    const flag = this.inspection && (s.isInspected || s.status !== 'Cancelled')
      ? `<span class="sess-pill__flag ${s.isInspected ? 'is-done' : 'is-todo'}" aria-hidden="true">
           <i class="fa-solid ${s.isInspected ? 'fa-circle-check' : 'fa-clock'}"></i>
         </span>`
      : '';

    return {
      html: `
        <div class="sess-pill" title="${escapeHtml([s.title, course, who, target].filter(Boolean).join('\n'))}">
          <span class="sess-pill__head">
            <span class="sess-pill__time">${escapeHtml(time)}</span>
            <span class="sess-pill__title">${escapeHtml(s.title)}</span>
            ${flag}
          </span>
          ${roomy && course ? `<span class="sess-pill__meta sess-pill__meta--course">${escapeHtml(course)}</span>` : ''}
          ${spacious && who ? `<span class="sess-pill__meta">${escapeHtml(who)}</span>` : ''}
          ${ample && target ? `<span class="sess-pill__meta sess-pill__meta--target">${escapeHtml(target)}</span>` : ''}
        </div>`,
    };
  }

  /** « Anglais · 8ème de base » : la matière, puis le niveau. */
  private courseOf(s: CalendarSession): string {
    return [this.subjectChoice.subjectsOf(s), this.subjectChoice.levelsOf(s.subjects)]
      .filter(Boolean)
      .join(' · ');
  }
}

/** Le contenu d'événement est injecté en HTML : titres et noms doivent être neutralisés. */
function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
